import { useEffect, useId, useState } from 'react';
import { Button, Dialog } from '@nyce/admin-kit/ui';
import { TabPanel } from '@nyce/admin-kit/console';
import { ErrorBox, Loading, Tabs } from '../../components/common';
import type { PrintJob } from '../api/client';
import { renderPdf, type PdfPages } from '../pdf';

/** Pages drawn at most; the rest is in the download. */
const MAX_PAGES = 20;
/** Characters of raw print data shown at most. */
const MAX_TEXT = 200_000;

/**
 * Print types with a preview: a PDF as its pages, an FR job (prepared FastReport report) as the PDF the hub renders
 * from it, raw printer data (ZPL, ESC/P, …) as text — ZPL also as its labels, rendered by the hub.
 */
export function previewKind(printType: string | null | undefined): 'pdf' | 'fr' | 'text' | null {
  const type = (printType ?? '').toUpperCase();
  return type === 'PDF' ? 'pdf' : type === 'FR' ? 'fr' : type === 'RAW' ? 'text' : null;
}

export const jobBytes = (job: PrintJob) => Uint8Array.from(atob(job.data ?? ''), (c) => c.charCodeAt(0));

/** Raw print data is ZPL when it holds a complete label (`^XA … ^XZ`), as the hub decides. */
export function isZpl(bytes: Uint8Array): boolean {
  const text = new TextDecoder('latin1').decode(bytes).toUpperCase();
  const start = text.indexOf('^XA');
  return start >= 0 && text.indexOf('^XZ', start) > start;
}

/** Raw printer data as text: control characters other than line breaks and tabs as their visible symbols (␛, ␂, …). */
export function rawText(bytes: Uint8Array): { text: string; truncated: boolean } {
  const decoded = new TextDecoder('utf-8').decode(bytes);
  const truncated = decoded.length > MAX_TEXT;
  const text = (truncated ? decoded.slice(0, MAX_TEXT) : decoded).replace(
    // eslint-disable-next-line no-control-regex
    /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g,
    (c) => String.fromCharCode(c === '\x7f' ? 0x2421 : 0x2400 + c.charCodeAt(0))
  );
  return { text, truncated };
}

type ZplTab = 'labels' | 'raw';
const ZPL_TABS: { value: ZplTab; label: string }[] = [
  { value: 'labels', label: 'Labels' },
  { value: 'raw', label: 'Raw data' },
];

/** One job's print data, fetched when opened: PDF pages, ZPL labels or raw text, with its download. */
export function JobPreview({
  jobId,
  load,
  loadPdf,
  loadLabels,
  onDownload,
  onClose,
}: {
  jobId: number;
  load: (id: number) => Promise<PrintJob>;
  /** An FR job as PDF. */
  loadPdf: (id: number) => Promise<Uint8Array>;
  /** A ZPL job as PDF (one page per label) and how many labels it holds. */
  loadLabels: (id: number) => Promise<{ pdf: Uint8Array; labels: number | null }>;
  onDownload: (job: PrintJob) => void;
  onClose: () => void;
}) {
  const [job, setJob] = useState<PrintJob | null>(null);
  const [pdf, setPdf] = useState<PdfPages | null>(null);
  const [raw, setRaw] = useState<{ text: string; truncated: boolean } | null>(null);
  const [zpl, setZpl] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [tab, setTab] = useState<ZplTab>('labels');
  const [error, setError] = useState<string | null>(null);
  const tabs = useId();

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const j = await load(jobId);
        if (!live) return;
        setJob(j);
        const kind = previewKind(j.printType);
        if (kind === 'text') {
          const bytes = jobBytes(j);
          setRaw(rawText(bytes));
          if (!isZpl(bytes)) return;
          setZpl(true);
          try {
            const labels = await loadLabels(jobId);
            const pages = await renderPdf(labels.pdf, MAX_PAGES);
            // the hub renders the first labels only; the header counts them all
            if (live) setPdf({ ...pages, total: Math.max(pages.total, labels.labels ?? 0) });
          } catch (e) {
            if (live) setLabelError((e as Error).message || 'The labels could not be rendered.');
          }
        } else {
          const bytes = kind === 'fr' ? await loadPdf(jobId) : jobBytes(j);
          const pages = await renderPdf(bytes, MAX_PAGES);
          if (live) setPdf(pages);
        }
      } catch (e) {
        if (live) setError((e as Error).message || 'The print data could not be shown.');
      }
    })();
    return () => {
      live = false;
    };
  }, [jobId, load, loadPdf, loadLabels]);

  const title = `Job #${jobId}${job?.fileName ? ` · ${job.fileName}` : ''}`;
  const unit = zpl ? 'labels' : 'pages';
  const pages = pdf && (
    <div className={zpl ? 'pq-pages pq-labels' : 'pq-pages'}>
      {pdf.pages.map((src, i) => (
        <img key={i} src={src} alt={`${zpl ? 'Label' : 'Page'} ${i + 1} of ${pdf.total}`} />
      ))}
      {pdf.total > pdf.pages.length && (
        <p className="nyk-ui-hint">
          {pdf.total - pdf.pages.length} more {unit}: download the job to see them all.
        </p>
      )}
    </div>
  );
  const text = raw && (
    <>
      <pre className="pq-raw" data-testid="pq-raw">
        {raw.text}
      </pre>
      {raw.truncated && <p className="nyk-ui-hint">Shortened: download the job for all of it.</p>}
    </>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      maxWidth={960}
      data-testid="pq-preview"
      footer={
        <Button variant="secondary" disabled={!job} onClick={() => job && onDownload(job)}>
          Download
        </Button>
      }
    >
      <ErrorBox error={error} />
      <Loading loading={!error && !pdf && !raw} />
      {zpl ? (
        <>
          <Tabs id={tabs} value={tab} onChange={setTab} options={ZPL_TABS} aria-label="ZPL preview" />
          <TabPanel tabs={tabs} value={tab} className="pq-tab">
            {tab === 'labels' ? (
              <>
                <Loading loading={!pdf && !labelError} />
                {labelError && <p className="nyk-ui-hint">The labels could not be rendered: {labelError}</p>}
                {pages}
                {pdf && (
                  <p className="nyk-ui-hint">
                    Drawn at 203 dpi; the printer's own fonts are approximated, barcodes and graphics are
                    exact.
                  </p>
                )}
              </>
            ) : (
              text
            )}
          </TabPanel>
        </>
      ) : (
        <>
          {pages}
          {text}
        </>
      )}
    </Dialog>
  );
}
